import React from "react";
import { interpolate, useCurrentFrame, useVideoConfig } from "remotion";
import type { TerminalTab } from "../Root";

// Method color coding
const METHOD_COLORS: Record<string, string> = {
  GET: "#A6E3A1",
  POST: "#F9E2AF",
  PUT: "#89B4FA",
  PATCH: "#CBA6F7",
  DELETE: "#F38BA8",
};

type TerminalWindowDarkProps = {
  children?: React.ReactNode;
  title?: string;
  opacity?: number;
  scale?: number;
  width?: number | string;
  height?: number | string;
  tabs?: TerminalTab[];
  /** Frames each tab stays visible */
  tabDurationFrames?: number;
  /** Render function receiving active tab index — used instead of children when tabs provided */
  renderTab?: (activeIndex: number) => React.ReactNode;
};

export const TerminalWindowDark: React.FC<TerminalWindowDarkProps> = ({
  children,
  title = "Terminal",
  opacity = 1,
  scale = 1,
  width = "100%",
  height = "100%",
  tabs,
  tabDurationFrames = 100,
  renderTab,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const hasTabs = tabs && tabs.length > 0;

  // Auto-cycle through tabs based on frame
  let activeTabIndex = 0;
  if (hasTabs && tabs.length > 1) {
    activeTabIndex = Math.floor(frame / tabDurationFrames) % tabs.length;
  }

  // Fade transition between tabs
  const tabLocalFrame = frame % tabDurationFrames;
  const fadeIn = interpolate(tabLocalFrame, [0, 8], [0, 1], {
    extrapolateRight: "clamp",
  });
  const fadeOut = interpolate(
    tabLocalFrame,
    [tabDurationFrames - 8, tabDurationFrames],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  const tabOpacity = hasTabs && tabs.length > 1 ? Math.min(fadeIn, fadeOut) : 1;

  return (
    <div
      style={{
        width,
        height,
        opacity,
        transform: `scale(${scale})`,
        borderRadius: 14,
        overflow: "hidden",
        backgroundColor: "#1E1E2E",
        border: "1px solid #313244",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Title bar */}
      <div
        style={{
          backgroundColor: "#181825",
          borderBottom: "1px solid #313244",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
      >
        {/* Traffic lights + title row */}
        <div
          style={{
            height: 40,
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
            gap: 8,
          }}
        >
          {/* Traffic lights */}
          <div style={{ display: "flex", gap: 7 }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#F38BA8",
              }}
            />
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#F9E2AF",
              }}
            />
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#A6E3A1",
              }}
            />
          </div>
          <div
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: 13,
              fontWeight: 500,
              color: "#6C7086",
              fontFamily: "system-ui, -apple-system, sans-serif",
            }}
          >
            {hasTabs ? (tabs[activeTabIndex]?.label || title) : title}
          </div>
          {/* Spacer to balance the dots */}
          <div style={{ width: 55 }} />
        </div>

        {/* Tab bar (only if multiple tabs) */}
        {hasTabs && tabs.length > 1 && (
          <div
            style={{
              display: "flex",
              gap: 2,
              padding: "0 14px 6px",
              overflowX: "hidden",
              flexWrap: "nowrap",
            }}
          >
            {tabs.map((tab, idx) => {
              const isActive = idx === activeTabIndex;
              const method = tab.method.toUpperCase();
              const methodColor = METHOD_COLORS[method] || "#CDD6F4";
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "3px 8px",
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: "monospace",
                    backgroundColor: isActive
                      ? "rgba(205,214,244,0.1)"
                      : "transparent",
                    opacity: isActive ? 1 : 0.4,
                    transition: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: methodColor }}>{method}</span>
                  <span style={{ color: "#6C7086" }}>
                    {tab.route.length > 18
                      ? tab.route.slice(0, 18) + "…"
                      : tab.route}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Content area */}
      <div
        style={{
          flex: 1,
          padding: "24px 28px",
          overflow: "hidden",
          opacity: tabOpacity,
        }}
      >
        {renderTab ? renderTab(activeTabIndex) : children}
      </div>
    </div>
  );
};
